import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { useUser } from '../../app/session';
import { toast } from '../../components/toasts';
import { Button, ErrorText, Field, Modal, Textarea } from '../../components/ui';
import { post } from '../../lib/api';
import { useOperators, usePresence } from '../../lib/queries';
import { AttendanceDetails } from '../../lib/types';

/**
 * Transfere para um operador (só ele assume) ou devolve para a fila. Quem tem a opção "transferir de
 * outros" também transfere o atendimento de outro operador ou redireciona um ainda não assumido.
 */
export function TransferModal({ attendance, onClose, onTransferred }: { attendance: AttendanceDetails; onClose: () => void; onTransferred: () => void }) {
  const user = useUser();
  const qc = useQueryClient();
  const operators = (useOperators().data ?? []).filter((o) => o.id !== attendance.operatorId);
  const online = usePresence().data;
  const [target, setTarget] = useState<string | null | undefined>(undefined);
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const own = attendance.status === 'em_atendimento' && attendance.operatorId === user.id;
  const holder = attendance.operatorName ?? 'outro operador';

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await post(`/attendances/${attendance.id}/transfer`, { toUserId: target ?? null, note: note.trim() || undefined });
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      toast(target ? 'Atendimento transferido' : 'Atendimento devolvido para a fila', 'ok');
      onTransferred();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const item = (id: string | null, label: string, isOnline?: boolean) => (
    <button
      key={id ?? 'fila'}
      onClick={() => setTarget(id)}
      className={clsx('flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-0', target === id ? 'bg-accent/10' : 'hover:bg-muted')}
    >
      {id && <span className={clsx('size-2 rounded-full', isOnline ? 'bg-ok' : 'bg-border')} title={isOnline ? 'online' : 'offline'} />}
      {label}
    </button>
  );

  return (
    <Modal
      open
      title="Transferir atendimento"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={target === undefined} loading={saving} onClick={() => void submit()}>
            Transferir
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-fg-soft">
        {own
          ? 'O cronômetro deste atendimento para; quem recebe assume e abre uma sessão nova.'
          : attendance.status === 'em_atendimento'
            ? `O atendimento está com ${holder}: a sessão dele é encerrada e o atendimento sai da tela dele. Quem recebe assume e abre uma sessão nova.`
            : 'O atendimento ainda não foi assumido: ele vai para quem você escolher (ou volta para a fila).'}
      </p>
      <div className="mb-3 max-h-64 overflow-y-auto rounded-md border border-border">
        {attendance.status !== 'na_fila' && item(null, 'Devolver para a fila (qualquer operador assume)')}
        {operators
          .filter((o) => !own || o.id !== user.id)
          .map((o) => item(o.id, o.id === user.id ? `${o.name} (você)` : o.name, online?.has(o.id)))}
      </div>
      <Field label="Observação" hint="(opcional, fica no histórico da transferência)">
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
      </Field>
      <ErrorText error={error} />
    </Modal>
  );
}
